import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { UploadCloud, FileText, AlertCircle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const DATE_RE = /(\d{4}-\d{2}-\d{2})/;

function extractDate(name = "") {
  const m = name.match(DATE_RE);
  return m ? m[1] : "";
}

function SingleUpload() {
  const [file, setFile] = useState(null);
  const [manualDate, setManualDate] = useState("");
  const [autoDate, setAutoDate] = useState("");
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  const handleFiles = (files) => {
    if (!files?.length) return;
    const f = files[0];
    setFile(f);
    const d = extractDate(f.name);
    setAutoDate(d);
    if (!d) setManualDate("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    if (!autoDate && !manualDate) {
      toast.error("Date missing. Provide manual date (yyyy-mm-dd).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (!autoDate && manualDate) fd.append("manual_date", manualDate);
      const { data } = await api.post("/upload/single", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Uploaded: ${data.file.total_rows} rows • ${data.file.dup_all} duplicates`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setAutoDate("");
    setManualDate("");
    setResult(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white">
        <form onSubmit={onSubmit} className="space-y-5" data-testid="single-upload-form">
          <div
            data-testid="upload-dropzone"
            className={`drop-zone ${drag ? "active" : ""} rounded-sm p-10 text-center cursor-pointer`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInput.current?.click()}
          >
            <UploadCloud className="mx-auto text-[#002FA7]" size={40} />
            <div className="mt-3 font-display text-lg font-semibold text-gray-950">
              {file ? file.name : "Drop CSV or Excel here"}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {file ? `${(file.size / 1024).toFixed(1)} KB` : "or click to browse"}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              data-testid="single-file-input"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {file && (
            <div className="border border-gray-200 rounded-sm p-4 bg-gray-50">
              <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">Detected Date</div>
              {autoDate ? (
                <div className="flex items-center gap-2 mt-1 text-[#00C04B]" data-testid="auto-date-detected">
                  <CheckCircle2 size={16} />
                  <span className="font-mono font-semibold">{autoDate}</span>
                  <span className="text-xs text-gray-500">— from filename</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mt-1 text-[#FF3B30]">
                    <AlertCircle size={16} />
                    <span className="text-sm">No yyyy-mm-dd date in filename. Please enter manually.</span>
                  </div>
                  <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-gray-700 mt-3 block">Manual Date</Label>
                  <Input
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    className="mt-1 rounded-sm h-10"
                    data-testid="manual-date-input"
                    required
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading || !file}
              className="flex-1 h-11 rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white"
              data-testid="single-upload-submit"
            >
              {loading ? "Processing…" : "Upload & Analyze"}
            </Button>
            {file && (
              <Button type="button" variant="outline" onClick={reset} className="rounded-sm h-11">
                <X size={16} className="mr-1" /> Clear
              </Button>
            )}
          </div>
        </form>
      </Card>

      <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white" data-testid="single-upload-result">
        <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Result</div>
        {!result ? (
          <div className="mt-3 text-gray-500 text-sm">Upload a file to see results: total rows, new leads, and duplicates against your existing database.</div>
        ) : (
          <div className="mt-4 space-y-5">
            <div>
              <div className="text-xs text-gray-500 mono">{result.file.original_name}</div>
              <div className="font-display text-2xl font-semibold text-gray-950 mt-1">Source: <span className="font-mono">{result.file.source_date}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="border border-gray-200 rounded-sm p-3">
                <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">Total</div>
                <div className="font-mono text-xl font-semibold text-gray-950 mt-1">{result.file.total_rows}</div>
              </div>
              <div className="border border-gray-200 rounded-sm p-3 bg-[#00C04B]/5">
                <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">New</div>
                <div className="font-mono text-xl font-semibold text-[#00C04B] mt-1">{result.file.new_leads}</div>
              </div>
              <div className="border border-gray-200 rounded-sm p-3 bg-[#FF3B30]/5">
                <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">Duplicates</div>
                <div className="font-mono text-xl font-semibold text-[#FF3B30] mt-1">{result.file.dup_all}</div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[["5d", result.file.dup_5], ["30d", result.file.dup_30], ["60d", result.file.dup_60], ["90d", result.file.dup_90]].map(([k, v]) => (
                <div key={k} className="border border-gray-200 rounded-sm p-2 text-center">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">Last {k}</div>
                  <div className="font-mono text-lg font-semibold text-gray-950">{v}</div>
                </div>
              ))}
            </div>
            {result.duplicate_samples?.length > 0 && (
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2">Duplicate Matches (showing up to 50)</div>
                <div className="border border-gray-200 rounded-sm max-h-64 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                        <th className="text-left px-3 py-2">Phone</th>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Matched On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.duplicate_samples.map((d, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono">{d.phone}</td>
                          <td className="px-3 py-2">{d.first_name} {d.last_name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-700">{d.matched_dates?.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function BulkUpload() {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inp = useRef(null);

  const onPick = (list) => {
    if (!list?.length) return;
    const arr = Array.from(list).map((f) => ({
      file: f,
      detectedDate: extractDate(f.name),
    }));
    setFiles(arr);
    setResults([]);
  };

  const start = async () => {
    if (!files.length) return toast.error("Pick one or more files");
    setLoading(true);
    setResults([]);
    // Process one-by-one for clear per-file results
    const out = [];
    for (const item of files) {
      if (!item.detectedDate) {
        out.push({ filename: item.file.name, status: "skipped", error: "No yyyy-mm-dd in filename" });
        setResults([...out]);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        const { data } = await api.post("/upload/single", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push({ filename: item.file.name, status: "ok", file: data.file });
      } catch (err) {
        out.push({ filename: item.file.name, status: "error", error: formatApiError(err.response?.data?.detail) || err.message });
      }
      setResults([...out]);
    }
    setLoading(false);
    toast.success(`Processed ${out.length} files`);
  };

  const ok = results.filter((r) => r.status === "ok").length;
  const err = results.filter((r) => r.status === "error" || r.status === "skipped").length;

  return (
    <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white">
      <div
        data-testid="bulk-dropzone"
        className={`drop-zone ${drag ? "active" : ""} rounded-sm p-8 text-center cursor-pointer`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onPick(e.dataTransfer.files); }}
        onClick={() => inp.current?.click()}
      >
        <UploadCloud className="mx-auto text-[#002FA7]" size={36} />
        <div className="mt-2 font-display text-lg font-semibold text-gray-950">Drop multiple files</div>
        <div className="text-sm text-gray-500 mt-1">CSV / XLSX — files MUST contain yyyy-mm-dd in their name</div>
        <input ref={inp} type="file" multiple accept=".csv,.xlsx,.xls" className="hidden" data-testid="bulk-file-input" onChange={(e) => onPick(e.target.files)} />
      </div>

      {files.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Queued: {files.length} files</div>
            <Button
              onClick={start}
              disabled={loading}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white"
              data-testid="bulk-start-button"
            >
              {loading ? "Processing…" : "Start Bulk Upload"}
            </Button>
          </div>

          <div className="border border-gray-200 rounded-sm max-h-[460px] overflow-auto" data-testid="bulk-results-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Rows</th>
                  <th className="text-left px-3 py-2">New</th>
                  <th className="text-left px-3 py-2">Dup (5/30/60/90/All)</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {files.map((it, idx) => {
                  const res = results[idx];
                  return (
                    <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 truncate max-w-[260px]" title={it.file.name}>
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-gray-400" />
                          <span className="truncate">{it.file.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{it.detectedDate || "—"}</td>
                      <td className="px-3 py-2 font-mono">{res?.file?.total_rows ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-[#00C04B]">{res?.file?.new_leads ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {res?.file ? `${res.file.dup_5}/${res.file.dup_30}/${res.file.dup_60}/${res.file.dup_90}/${res.file.dup_all}` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {!res ? <span className="text-gray-400 text-xs">Queued</span> :
                          res.status === "ok" ? <span className="text-[#00C04B] text-xs font-medium">✓ Done</span> :
                          res.status === "skipped" ? <span className="text-amber-600 text-xs" title={res.error}>Skipped</span> :
                          <span className="text-[#FF3B30] text-xs" title={res.error}>Error</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {results.length > 0 && (
            <div className="mt-3 text-sm text-gray-600">
              <span className="text-[#00C04B] font-semibold">{ok} succeeded</span> · <span className="text-[#FF3B30] font-semibold">{err} failed/skipped</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function Upload() {
  const [params] = useSearchParams();
  const initial = params.get("mode") === "bulk" ? "bulk" : "single";

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="upload-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Ingest</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Upload Leads</h1>
        <p className="text-gray-600 mt-2">CSV or Excel. The portal extracts the date from the filename (yyyy-mm-dd) automatically.</p>
      </div>

      <Tabs defaultValue={initial} className="w-full" data-testid="upload-tabs">
        <TabsList className="rounded-sm bg-gray-100">
          <TabsTrigger value="single" data-testid="tab-single" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:text-[#002FA7]">Single File</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:text-[#002FA7]">Bulk Historical</TabsTrigger>
        </TabsList>
        <TabsContent value="single" className="mt-6">
          <SingleUpload />
        </TabsContent>
        <TabsContent value="bulk" className="mt-6">
          <BulkUpload />
        </TabsContent>
      </Tabs>
    </div>
  );
}
